/**
 * TopBarContext — global top bar slot injection mechanism.
 *
 * Pages call `useTopBarLeft(node)` / `useTopBarActions(node)` to inject
 * content into the shared GlobalTopBar rendered by AppLayout. Injection
 * is lifecycle-bound: cleanup clears the slot when the page unmounts,
 * so navigating between routes never leaves stale actions behind.
 *
 * Implementation: an external store (not React Context state). The store
 * is updated from a `useEffect`, but its `emit()` only re-renders
 * `GlobalTopBar` (which subscribes via `useSyncExternalStore`). The
 * page itself does not subscribe, so a store update does not trigger a
 * re-render of the page — breaking the otherwise infinite loop
 * (page setState → Context state change → page re-render → new node → …).
 */

import {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from "react";

interface TopBarState {
  left: ReactNode;
  right: ReactNode;
}

const subscribers = new Set<() => void>();
let state: TopBarState = { left: null, right: null };

function emit(): void {
  for (const cb of subscribers) cb();
}

const store = {
  subscribe(cb: () => void): () => void {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  },
  getSnapshot(): TopBarState {
    return state;
  },
  setLeft(node: ReactNode): void {
    state = { ...state, left: node };
    emit();
  },
  setRight(node: ReactNode): void {
    state = { ...state, right: node };
    emit();
  },
};

/**
 * Provider kept for API compatibility — renders children directly.
 * State lives in the module-level store above, not in component state.
 */
export function TopBarProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

interface TopBarApi {
  setLeft: (node: ReactNode) => void;
  setRight: (node: ReactNode) => void;
}

const topBarApi: TopBarApi = {
  setLeft: store.setLeft,
  setRight: store.setRight,
};

const TopBarApiContext = createContext<TopBarApi>(topBarApi);

/** Inject content into the top bar's left slot. Pass `null` to clear. */
export function useTopBarLeft(node: ReactNode): void {
  const api = useContext(TopBarApiContext);
  useEffect(() => {
    api.setLeft(node);
    return () => api.setLeft(null);
  }, [api, node]);
}

/** Inject content into the top bar's right slot. Pass `null` to clear. */
export function useTopBarActions(node: ReactNode): void {
  const api = useContext(TopBarApiContext);
  useEffect(() => {
    api.setRight(node);
    return () => api.setRight(null);
  }, [api, node]);
}

/** Read current slots. Subscribes to the store — only GlobalTopBar uses this. */
export function useTopBarSlots(): TopBarState {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
